import React from "react";
import { css } from "@emotion/css";
import { useNavigate } from "react-router-dom";
import { VideoRecommendation } from "./api";
import { handleVideoClick } from "./utils/videoUtils";
import { useLazyThumbnail } from "./hooks/useLazyThumbnail";
import { formatDuration } from "./utils/formatDuration";
import RecommendationActionsWrapper from "./components/RecommendationActionsWrapper";
import { ResourceType } from "./types/activity_types";

interface VideoRecommendationCardProps {
  video: VideoRecommendation;
  index?: number;
  onboardingMode?: boolean;
  onUnbookmark?: () => void;
  hideMoreOptions?: boolean;
}

const VideoRecommendationCard: React.FC<VideoRecommendationCardProps> = ({ video, index = 0, onboardingMode = false, onUnbookmark, hideMoreOptions = false }) => {
  // Every 4th video (0-based index) will have a large layout
  const isLargeLayout = index % 4 === 0;
  const { ref, isVisible } = useLazyThumbnail();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const styles = {
    card: css`
      display: flex;
      flex-direction: column;
      width: 100%;
      background: ${colors.cardBackground};
      border-radius: 12px;
      overflow: hidden;
      padding-bottom: 4px;
      transition: all 0.2s ease;
      cursor: pointer;

      ${breakpoints.desktop} {
        flex-direction: ${isLargeLayout ? "column" : "row"};
        height: ${isLargeLayout
          ? layouts.large.height
          : layouts.standard.height};
      }

      &:hover {
        transform: translateY(-2px);
        background: ${colors.hover};

        img {
          transform: scale(1.05);
        }
      }
    `,
    thumbnailContainer: css`
      position: relative;
      width: 100%;
      padding-top: 56.25%; /* 16:9 aspect ratio for mobile */
      background: ${colors.background};
      overflow: hidden;

      ${breakpoints.desktop} {
        width: ${isLargeLayout ? "100%" : layouts.standard.thumbnailWidth};
        min-width: ${isLargeLayout ? "100%" : layouts.standard.thumbnailWidth};
        padding-top: ${isLargeLayout ? layouts.large.thumbnailPaddingTop : "0"};
        height: ${isLargeLayout ? "auto" : "100%"};
      }
    `,
    thumbnail: css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    `,
    duration: css`
      position: absolute;
      bottom: 8px;
      right: 8px;
      background: ${colors.duration};
      color: ${colors.textPrimary};
      padding: 3px 6px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      z-index: 1;
    `,
    content: css`
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 4px;

      ${breakpoints.desktop} {
        flex: 1;
        min-width: 0; /* Enable text truncation */
        justify-content: center;
      }
    `,
    title: css`
      margin: 0;
      font-size: 18px;
      font-weight: 500;
      line-height: 1.4;
      color: ${colors.textPrimary};
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `,
    description: css`
      margin: 4px 0 0;
      color: ${colors.textTertiary};
      font-size: 16px;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 9;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `,
    channelName: css`
      margin: 0;
      margin-top: 3px;
      color: #b8b8b8; /* Lighter gray to differentiate from other secondary text */
      font-size: 14px;
      font-weight: 500; /* Slightly bolder */
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: initial;
      display: inline-block;

      @media (max-width: 769px) {
        max-width: 80%;
      }
    `,
  };

  const handleClick = async () => {
    if (loading || onboardingMode) return;
    setLoading(true);
    
    // Navigate to the video page (tracking/storing the "interacted" UserActivity will happen in SummarySession to not have the user waiting here)
    await handleVideoClick(video.id, navigate, true);
    setLoading(false);
  };

  return (
    <RecommendationActionsWrapper
      recommendation={video}
      channelName={video.channel_name}
      channelId={video.channel_id}
      resourceId={video.id}
      resourceType={ResourceType.YT_FULL_VIDEO}
      onboardingMode={onboardingMode}
      onUnbookmark={onUnbookmark}
      hideMoreOptions={hideMoreOptions}
    >
      <div ref={ref} className={styles.card} onClick={handleClick}>
        <div className={styles.thumbnailContainer}>
          {isVisible && (
            <img
              src={video.thumbnail_url}
              alt={video.title}
              className={styles.thumbnail}
            />
          )}
          <span className={styles.duration}>
            {formatDuration(video.duration)}
          </span>
        </div>
        <div className={styles.content}>
          <h3 className={styles.title}>{video.title}</h3>
          <p className={styles.channelName}>{video.channel_name}</p>
          {onboardingMode && video.marketed_description && (
            <>
            <hr/>
            <p className={styles.description}>{video.marketed_description}</p>
            </>
          )}
        </div>
      </div>
    </RecommendationActionsWrapper>
  );
};

// Dark theme colors
const colors = {
  background: "#1a1a1a",
  cardBackground: "#242424",
  textPrimary: "#ffffff",
  textSecondary: "#a0a0a0",
  textTertiary: "#c4c4c4",
  hover: "#2a2a2a",
  duration: "rgba(0, 0, 0, 0.8)",
};

// Breakpoints
const breakpoints = {
  mobile: "@media (max-width: 767px)",
  desktop: "@media (min-width: 768px)",
};

// Layout variations
const layouts = {
  standard: {
    height: "140px",
    thumbnailWidth: "250px",
  },
  large: {
    height: "auto",
    thumbnailPaddingTop: "56.25%", // 16:9 aspect ratio
  },
};

export default VideoRecommendationCard;
